import { useHistory } from 'react-router';
import { Button } from '../../../shared/formElements/button';
import * as Yup from 'yup';
import {
  Form,
  FormContainer,
  FormSectionContainer,
  ButtonGroupContainer,
  SectionContainer,
  LoadingSpinner,
  Line,
  ImageSplitFormSection,
} from '../../../shared/Layout.styles';
import { Markdown } from '../../../shared/markdown';
import {
  activateCardConstants,
} from '../../../utils/constants/activate';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useContextDispatch, useContextState } from '../../context/context';
import { ActivateCardInputs } from './sections';
import { useCallback, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { IconSectionCard } from '../../../shared/iconSectionCardSection';
import { globalConstants } from '../../../utils/constants/global';
import axios from 'axios';
const { activationCode, firstName, lastName, phoneNumber, termsConditionsCheck } =
  activateCardConstants.validations;

declare global {
  interface Window {
    grecaptcha: any;
  }
}

export const ActivateCard = () => {
  const dispatch = useContextDispatch();
  const { loading} = useContextState();
  const [captchaToken, setCaptchaToken] = useState<string>("");
  const [isCaptchaCompleted, setCaptchaCompleted] = useState(false);
  const recaptcha = useRef<ReCAPTCHA>(null);

  const resetCaptcha = useCallback(() => {
    recaptcha.current?.reset();
    setCaptchaCompleted(false);
    setCaptchaToken("");
  }, []);

  const handleCaptchaChange = useCallback((value: string | null) => {
    setCaptchaCompleted(Boolean(value));
    setCaptchaToken(value || "");
  }, []);

  const [serverErrors, setServerErrors] = useState('');

  const { heading, subHeading, activateCardText, activateCardButtonText } =
    activateCardConstants;
  const { expiredCardMessage } = globalConstants;

  const { push } = useHistory();

  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: yupResolver(validationSchema),
    shouldFocusError: false,
    defaultValues: {
      activationCode: '',
      email: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      termsConditionsCheck: false,
    },
  });

  const onSubmit = useCallback(async (formData: any) => {
    if (!isCaptchaCompleted) {
      setServerErrors('Captcha not verified');
      return;
    }
    try {
      dispatch('START_LOADING');
      setServerErrors('');
      const data = {
        fname: formData.firstName,
        lname: formData.lastName,
        eaid: formData.activationCode,
        captchaToken: captchaToken,
      };
      await axios.post('/v1/activatecard', data,  {
        headers: {
          'X-API-Key': process.env.REACT_APP_VAULT_API_KEY || ''
        },
      });
      dispatch('STOP_LOADING');
      push('/success');
    } catch (err:any) {
      console.log('err', err);
      resetCaptcha()
     if (err.response.status === 700) {
        setServerErrors(expiredCardMessage);
      } else {
        setServerErrors(err.response.data.message);
      }
      dispatch('STOP_LOADING');
    }
  }, [captchaToken, dispatch, expiredCardMessage, isCaptchaCompleted, push, resetCaptcha]);

  return (
    <>
      <Line />
      <FormProvider {...methods}>
        <Form onSubmit={methods.handleSubmit(onSubmit)}>
          <SectionContainer>
            <Markdown children={heading} />
            <Markdown children={subHeading} />
            <Markdown children={activateCardText} />
            <FormContainer>
              <FormSectionContainer className="activateCardSection">
                <ActivateCardInputs />
                {serverErrors && (
                  <Markdown
                    children={serverErrors}
                    className="validationText"
                  />
                )}
                <ReCAPTCHA
                  ref={recaptcha}
                  sitekey="6LfU8UspAAAAAGYvuYdOkXrHcGGpuEXM4-hxH7Rh"
                  onChange={handleCaptchaChange}
                />
                <ButtonGroupContainer>
                  <Button
                    type="submit"
                    onClick={() => {
                      methods.handleSubmit(onSubmit);
                    }}
                  >
                    {loading ? <LoadingSpinner show /> : activateCardButtonText}
                  </Button>
                </ButtonGroupContainer>
              </FormSectionContainer>
              <FormSectionContainer>
                {process.env.REACT_APP_CARD_OR_ICONS === 'CARD' ? (
                  <ImageSplitFormSection
                    src={process.env.REACT_APP_CARD_FRONT ?? ''}
                  />
                ) : (
                  <IconSectionCard />
                )}
              </FormSectionContainer>
            </FormContainer>
          </SectionContainer>
        </Form>
      </FormProvider>
    </>
  );
};
const validationSchema = Yup.object().shape({
  activationCode: Yup.string()
    .required(activationCode.required).max(10)
    .matches(/^[a-zA-Z0-9_.-]*$/, {
      message: activationCode.format,
      excludeEmptyString: true,
    }),
  firstName: Yup.string()
    .required(firstName.required)
    .matches(/^([A-Za-z\s\-'".])*$/, {
      message: firstName.format,
      excludeEmptyString: true,
    }),
  lastName: Yup.string()
    .required(lastName.required)
    .matches(/^([A-Za-z\s\-'".])*$/, {
      message: lastName.format,
      excludeEmptyString: true,
    }),
  termsConditionsCheck: Yup.bool().oneOf([true], termsConditionsCheck.required),
});